import { create } from 'zustand';
import { db, auth } from '../lib/firebase';
import {
    collection, query, where, getDocs, doc, setDoc, updateDoc,
    arrayUnion, serverTimestamp, getDoc, onSnapshot
} from 'firebase/firestore';

const useContactStore = create((set, get) => ({
    contacts: [], // Array of User Objects
    sentRequests: [],
    receivedRequests: [], // Needs subcollection listener
    searchResults: [],
    loading: false,
    error: null,

    // Search for users by email (Exact match for privacy)
    searchUsers: async (email) => {
        set({ loading: true, error: null, searchResults: [] });
        try {
            // Assume 'users' collection has 'email' field indexed?
            // If not, this requires composite index or query might fail if not indexed.
            // But usually single field query works.
            const q = query(collection(db, 'users'), where('email', '==', email));
            const snap = await getDocs(q);
            const results = snap.docs.map(d => ({ uid: d.id, ...d.data() }));

            // Filter out self
            const selfUid = auth.currentUser?.uid;
            const filtered = results.filter(u => u.uid !== selfUid);

            set({ searchResults: filtered, loading: false });
        } catch (err) {
            console.error(err);
            set({ error: err.message, loading: false });
        }
    },

    // Send Connection Request
    sendRequest: async (targetUid) => {
        set({ loading: true });
        const selfUid = auth.currentUser?.uid;
        if (!selfUid) return;

        try {
            // Add to 'requests' subcollection of the TARGET user?
            // Or use a root 'friend_requests' collection?
            // Let's use root 'connections' logic or subcollections.
            // Simplest: `users/{targetUid}/requests/{selfUid}` doc.

            // 1. Create Request Doc on Target
            await setDoc(doc(db, 'users', targetUid, 'requests', selfUid), {
                from: selfUid,
                status: 'pending',
                timestamp: serverTimestamp()
            });

            // 2. Clear Search
            set({ searchResults: [], loading: false });
        } catch (err) {
            console.error(err);
            set({ error: err.message, loading: false });
        }
    },

    // Listen for Incoming Requests
    subscribeToRequests: () => {
        const selfUid = auth.currentUser?.uid;
        if (!selfUid) return;

        const q = query(collection(db, 'users', selfUid, 'requests'), where('status', '==', 'pending'));
        return onSnapshot(q, async (snap) => {
            // Need to fetch profile data for these requests
            const reqs = [];
            for (const d of snap.docs) {
                const data = d.data();
                const profileSnap = await getDoc(doc(db, 'users', data.from));
                if (profileSnap.exists()) {
                    reqs.push({ ...profileSnap.data(), requestId: d.id });
                }
            }
            set({ receivedRequests: reqs });
        });
    },

    acceptRequest: async (targetUid) => {
        const selfUid = auth.currentUser?.uid;
        if (!selfUid) return;
        set({ loading: true });

        try {
            // 1. Add to My Contacts (Array in user doc or subcollection?)
            // Let's use subcollection 'contacts' for scalability.
            await setDoc(doc(db, 'users', selfUid, 'contacts', targetUid), {
                uid: targetUid,
                timestamp: serverTimestamp()
            });

            // 2. Add Me to Their Contacts
            await setDoc(doc(db, 'users', targetUid, 'contacts', selfUid), {
                uid: selfUid,
                timestamp: serverTimestamp()
            });

            // 3. Delete Request
            await updateDoc(doc(db, 'users', selfUid, 'requests', targetUid), {
                status: 'accepted'
            });

            set({ loading: false });
        } catch (err) {
            console.error(err);
            set({ error: err.message, loading: false });
        }
    },

    // Listen to My Contacts
    subscribeToContacts: () => {
        const selfUid = auth.currentUser?.uid;
        if (!selfUid) return;

        return onSnapshot(collection(db, 'users', selfUid, 'contacts'), async (snap) => {
            const contacts = [];
            for (const d of snap.docs) {
                // Fetch full profile
                const profileSnap = await getDoc(doc(db, 'users', d.id));
                if (profileSnap.exists()) {
                    contacts.push(profileSnap.data());
                }
            }
            set({ contacts });
        });
    }
}));

export default useContactStore;
